const pendingByAccount = new Map();
const closingByAccount = new Map();

export async function waitForConfigurationWrites(accountId) {
  await Promise.all([pendingByAccount.get(accountId), closingByAccount.get(accountId)]);
}

export function createConfigAutosave({ config, accountId, api, onState, storage = localStorage }) {
  const requestConfig = { headers: { "X-Account-Id": accountId } };
  const draftKey = `fishit_config_draft:${config.user_id}`;
  const sessionId = `${Date.now()}:${Math.random()}`;
  let cfg = { ...config };
  let changes = new Map();
  let serverValues = { ...config };
  let revision = 0;
  let attemptedRevision = -1;
  let timer = null;
  let inFlight = null;
  let closed = false;
  let resetting = false;
  let retries = 0;
  let status = "saved";
  let error = "";

  const publish = () => onState({ cfg: { ...cfg }, status, error, busy: !!inFlight || resetting });
  const clearTimer = () => { clearTimeout(timer); timer = null; };
  const persist = () => {
    try {
      if (changes.size) {
        storage.setItem(draftKey, JSON.stringify({ sessionId, revision,
          changes: Object.fromEntries([...changes].map(([k, v]) => [k, v.value])),
          baseValues: Object.fromEntries([...changes].map(([k, v]) => [k, v.base])) }));
      } else {
        const saved = JSON.parse(storage.getItem(draftKey) || "null");
        if (saved?.sessionId === sessionId) storage.removeItem(draftKey);
      }
    } catch { /* A storage limit must not prevent saving to the server. */ }
  };
  const schedule = (ms = 2000) => {
    clearTimer();
    if (!closed && !resetting) timer = setTimeout(() => { void flush(); }, ms);
  };

  try {
    const draft = JSON.parse(storage.getItem(draftKey) || "null");
    for (const [key, value] of Object.entries(draft?.changes || {})) {
      if (Object.hasOwn(config, key) && key !== "enabled" && key !== "user_id") {
        changes.set(key, { value, revision: ++revision, base: Object.hasOwn(draft?.baseValues || {}, key) ? draft.baseValues[key] : config[key] });
        cfg[key] = value;
      }
    }
  } catch { /* Ignore an unreadable draft; keep the valid server configuration. */ }

  async function request(reset = false, resetRevision = revision) {
    const previous = pendingByAccount.get(accountId) || Promise.resolve();
    let savedRevision = revision;
    let success = false;
    let retryDelay = null;
    status = "saving";
    error = "";
    const task = previous.catch(() => {}).then(async () => {
      savedRevision = reset ? resetRevision : revision;
      attemptedRevision = savedRevision;
      try {
        const response = reset
          ? await api.post("/automation/config/reset?apply_live=true", null, requestConfig)
          : await api.patch("/automation/config", {
              changes: Object.fromEntries([...changes].map(([k, v]) => [k, v.value])),
              base_values: Object.fromEntries([...changes].map(([k, v]) => [k, v.base])),
            }, requestConfig);
        for (const [key, entry] of changes) {
          if (entry.revision <= savedRevision) changes.delete(key);
          else entry.base = response.data[key];
        }
        serverValues = { ...response.data };
        cfg = { ...response.data,
          ...Object.fromEntries([...changes].map(([k, v]) => [k, v.value])) };
        persist();
        retries = 0;
        status = changes.size ? "pending" : "saved";
        success = true;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const waiting = e?.response?.status === 409 && detail?.code === "config_apply_busy";
        status = waiting ? "waiting" : detail?.code === "config_conflict" ? "conflict" : "error";
        error = (typeof detail === "string" ? detail : detail?.message)
          || (Array.isArray(detail) ? "Ada nilai yang belum valid. Periksa pengaturan yang diubah." : "Gagal menyimpan. Perubahan masih disimpan sebagai draf di perangkat ini.");
        if (waiting) retryDelay = 30000;
        else if ((!e?.response || e.response.status >= 500) && retries < 3) {
          retryDelay = [5000, 15000, 30000][retries++];
        }
      }
    });
    pendingByAccount.set(accountId, task);
    inFlight = task;
    publish();
    await task;
    if (pendingByAccount.get(accountId) === task) pendingByAccount.delete(accountId);
    if (inFlight === task) inFlight = null;
    publish();
    if (changes.size && !resetting) {
      if (revision > savedRevision) schedule();
      else if (retryDelay != null) schedule(retryDelay);
    }
    return success;
  }

  async function flush() {
    clearTimer();
    if (resetting) return false;
    if (inFlight) {
      await inFlight;
      // The finishing request must not overwrite newer edits, or replay an
      // unchanged failed request just because several flush events arrived.
      await Promise.resolve();
      if (inFlight) return false;
      return changes.size && revision > attemptedRevision ? flush() : !changes.size;
    }
    if (!changes.size) return true;
    return request();
  }

  function update(key, value) {
    if (closed || key === "enabled" || key === "user_id") return;
    if (Object.is(cfg[key], value)) return;
    cfg = { ...cfg, [key]: value };
    changes.set(key, { value, revision: ++revision, base: changes.has(key) ? changes.get(key).base : serverValues[key] });
    retries = 0;
    status = "pending";
    error = "";
    persist();
    publish();
    schedule();
  }

  async function reset() {
    clearTimer();
    resetting = true;
    const resetRevision = revision;
    publish();
    if (inFlight) { await inFlight; await Promise.resolve(); }
    const success = await request(true, resetRevision);
    resetting = false;
    publish();
    if (changes.size && revision > resetRevision) schedule();
    return success;
  }

  async function reload() {
    if (inFlight || resetting) return false;
    clearTimer();
    resetting = true;
    publish();
    try {
      const response = await api.get("/automation/config", requestConfig);
      changes.clear();
      cfg = { ...response.data };
      serverValues = { ...cfg };
      // The owner explicitly chose to discard this draft.
      try { storage.removeItem(draftKey); } catch {}
      status = "saved";
      error = "";
      retries = 0;
      return true;
    } catch {
      status = "error";
      error = "Pengaturan terbaru belum bisa diambil. Draf tetap tersedia.";
      return false;
    } finally { resetting = false; publish(); }
  }

  function close() {
    closed = true;
    clearTimer();
    const finished = flush();
    closingByAccount.set(accountId, finished);
    finished.finally(() => {
      if (closingByAccount.get(accountId) === finished) closingByAccount.delete(accountId);
    });
    return finished;
  }

  if (changes.size) { status = "pending"; persist(); schedule(); }
  publish();
  return { update, flush, reset, reload, close, hasPending: () => !!changes.size || !!inFlight || resetting };
}
